import { useMediaQuery } from '@chakra-ui/react';
import { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAlert } from '@app/hooks';

import { QueryTableContext, QueryTableContextValue } from './context/query-table-context';
import { MobileTable } from './mobile-table';
import { TableBase } from './table-base';
import { QueryTableDataResponse, QueryTableParams, TableBaseProps, TableFilter, TableInitialState, TablePagination, TableSortValue } from './types';
import { filterMapping } from './utils';
import { useFilter } from './hooks/use-filter';
import { useSort } from './hooks/use-sort';
import { usePagination } from './hooks/use-pagination';

type QueryTableProps<I extends EntityBase = EntityBase> = Pick<TableBaseProps<I>, 
  'columns' | 
  'onCreateClick' |
  'title' |   
  'keyProperty' |
  'selectedRow' |
  'onRowClick' |
  'createMenu' | 
  'additionalToolbar' |
  'secondaryToolbar' |
  'renderGroup' |
  'onFilterChange' |
  'getRowBackgroundColor'
  > & TableInitialState<I> & {
    fetchData(params: QueryTableParams<I>): Promise<QueryTableDataResponse<I>>
    canDownload?: boolean
    ignoreQuery?: boolean
    noPagination?: boolean
} & PropsWithChildren;

export function QueryTable<I extends EntityBase = EntityBase>(props: QueryTableProps<I>) {
  const {
    fetchData,
    columns,
    children,
    canDownload,
    ignoreQuery,
    noPagination,
    onFilterChange: onFilterChangeProp,
    ...restProps
  } = props;
  const { pagination, setPagination } = usePagination({ initial: { page: 1, pageSize: 10 }, ignoreQuery });
  const { sort, setSort } = useSort<I>({ ignoreQuery, initial: props.initialSort });
  const { filter, setFilter } = useFilter<I>({ columns, ignoreQuery });
  const [loading, setLoading] = useState(false);
  const { info } = useAlert();

  const [data, setData] = useState<I[]>([]);
  const [total, setTotal] = useState(0);
  
  const fetch = useCallback(async (params: QueryTableParams<I>) => {
    setLoading(true);

    const { data, total } = await fetchData(params);

    setTotal(total);
    setData(data);
    setLoading(false);
  }, [fetchData]);

  const download = useCallback(async () => {
    setLoading(true);

    const { code, content, filename, message } = await fetchData({ 
      filter, 
      filterRaw: filterMapping<I>(filter, columns),
      sort: sort.name ? sort : { name: 'id', direction: 'asc' },
      pagination: { pageSize: 10, page: 1 },
      forDownload: true 
    });

    if (!content && message) {
      code === 200 && info(message);
      setLoading(false);
      return;
    }
    if (!content) {
      setLoading(false);
      return;
    }

    const linkElement = document.createElement('a');
    linkElement.href =
      'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' +
      content;
    linkElement.download = filename;
    document.body.appendChild(linkElement);
    linkElement.click(); // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
    document.body.removeChild(linkElement);
    setLoading(false);
  }, [columns, fetchData, filter, sort]);

  const onPaginationChange = useCallback((pagination: TablePagination) => {
    setPagination(pagination);
    fetch({
      pagination,
      filter,
      filterRaw: filterMapping<I>(filter, columns),
      sort,
    });
  }, [setPagination, fetch, filter, columns, sort]);

  const onSortChange = useCallback((sort: TableSortValue<I>) => {
    setSort(sort);
    fetch({
      pagination,
      filter,
      filterRaw: filterMapping<I>(filter, columns),
      sort,
    });
  }, [setSort, fetch, pagination, filter, columns]);

  const onFilterChange = useCallback((filter: TableFilter<I>[]) => {
    setPagination({ page: 1, pageSize: pagination.pageSize });
    setFilter(filter);
    onFilterChangeProp?.(filter);
    fetch({
      pagination: { page: 1, pageSize: pagination.pageSize },
      filter,
      filterRaw: filterMapping<I>(filter, columns),
      sort,
    });
  }, [setFilter, setPagination, pagination.pageSize, fetch, columns, sort]);

  useEffect(() => {
    fetch({
      pagination,
      filter,
      filterRaw: filterMapping<I>(filter, columns),
      sort,
    });
  }, []);

  const contextValue: QueryTableContextValue<I> = useMemo(() => ({
    columns,
    data,
    filter,
    sort,
    setFilter: onFilterChange,
    setSort,
    loading,
    pagination,
    setPagination,
    refetch: () => fetch({ 
      pagination, 
      sort, 
      filterRaw: filterMapping<I>(filter, columns), 
      filter 
    }),
    setData,
  }), [columns, data, filter, sort, onFilterChange, setSort, loading, fetch, pagination, setPagination]);

  const [isDesktop] = useMediaQuery('(min-width: 768px)', {
    fallback: true
  });
  
  if (!isDesktop) {
    return <QueryTableContext.Provider value={contextValue as unknown as QueryTableContextValue<EntityBase>} >
      <MobileTable 
        columns={columns}
        data={data} 
        pagination={noPagination ? undefined : pagination}
        total={total}
        sort={sort}
        filter={filter || []}
        loading={loading}
        onFilterChange={onFilterChange}
        onPaginationChange={onPaginationChange}
        onSortChange={onSortChange}
        {...restProps}  
      />
    </QueryTableContext.Provider>;
  }

  return <QueryTableContext.Provider value={contextValue as unknown as QueryTableContextValue<EntityBase>} >
    <TableBase<I> 
      columns={columns}
      data={data} 
      pagination={noPagination ? undefined : pagination}
      total={total}
      sort={sort}
      filter={filter || []}
      loading={loading}
      onFilterChange={onFilterChange}
      onPaginationChange={onPaginationChange}
      onSortChange={onSortChange}
      onDownload={canDownload ? download : undefined}
      {...restProps}  
    />
    {children}
  </QueryTableContext.Provider>;
}

export function useQueryTable() {
  return useContext(QueryTableContext);
}

QueryTable.useQueryTable = useQueryTable;
